import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, CalendarCheck, Loader2, Phone } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PHONE, PHONE_HREF } from "@/lib/images";

const TIME_SLOTS = [
  { id: "morning", label: "Morning", hint: "9am – 12pm" },
  { id: "afternoon", label: "Afternoon", hint: "12pm – 4pm" },
  { id: "evening", label: "Evening", hint: "4pm – 7pm" },
];

const SETTINGS = ["At Home", "Assisted Living", "Care Facility"];

export const BookingForm = ({ idPrefix = "booking-form" }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [date, setDate] = useState(undefined);
  const [slot, setSlot] = useState("");
  const [setting, setSetting] = useState("At Home");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error("Please choose a preferred date");
      return;
    }
    if (!slot) {
      toast.error("Please choose a preferred time of day");
      return;
    }
    setSubmitting(true);
    try {
      const slotLabel = TIME_SLOTS.find((s) => s.id === slot);
      await api.post("/consultations", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        date: format(date, "yyyy-MM-dd"),
        time_slot: `${slotLabel.label} (${slotLabel.hint})`,
        setting,
        notes: form.notes || undefined,
      });
      setConfirmed({ name: form.name, date, slotLabel, setting });
      toast.success("Consultation request received");
    } catch {
      toast.error(`Something went wrong — please call us at ${PHONE}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div
        data-testid={`${idPrefix}-confirmation`}
        className="rounded-3xl border border-sage/30 bg-sage-soft p-8 text-center sm:p-10"
      >
        <CalendarCheck className="mx-auto mb-4 h-14 w-14 text-sage" />
        <h3 className="mb-3 font-serif text-2xl font-semibold text-espresso sm:text-3xl">
          Thank you, {confirmed.name.split(" ")[0]}!
        </h3>
        <p className="mx-auto mb-6 max-w-md text-lg text-espresso/80">
          Your free consultation request for{" "}
          <strong>{format(confirmed.date, "EEEE, MMMM d, yyyy")}</strong> in the{" "}
          <strong>{confirmed.slotLabel.label.toLowerCase()}</strong> ({confirmed.setting}) has been
          received. We will be in touch shortly to confirm.
        </p>
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a href={PHONE_HREF} data-testid={`${idPrefix}-confirmation-call-button`} className="btn-primary">
            <Phone className="h-5 w-5" /> Call {PHONE}
          </a>
          <button
            data-testid={`${idPrefix}-book-another-button`}
            className="btn-outline"
            onClick={() => {
              setConfirmed(null);
              setForm({ name: "", phone: "", email: "", notes: "" });
              setDate(undefined);
              setSlot("");
            }}
          >
            Book Another Request
          </button>
        </div>
      </div>
    );
  }

  return (
    <form data-testid={idPrefix} onSubmit={submit} className="space-y-6">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor={`${idPrefix}-name`} className="input-label">
            Your name *
          </label>
          <input
            id={`${idPrefix}-name`}
            data-testid={`${idPrefix}-name-input`}
            className="input-field"
            placeholder="e.g. Karen Turner"
            value={form.name}
            onChange={set("name")}
            required
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-phone`} className="input-label">
            Phone number *
          </label>
          <input
            id={`${idPrefix}-phone`}
            data-testid={`${idPrefix}-phone-input`}
            type="tel"
            className="input-field"
            placeholder="(403) 555-0123"
            value={form.phone}
            onChange={set("phone")}
            required
          />
        </div>
      </div>

      <div>
        <label htmlFor={`${idPrefix}-email`} className="input-label">
          Email address *
        </label>
        <input
          id={`${idPrefix}-email`}
          data-testid={`${idPrefix}-email-input`}
          type="email"
          className="input-field"
          placeholder="you@example.com"
          value={form.email}
          onChange={set("email")}
          required
        />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <span className="input-label">Preferred date *</span>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid={`${idPrefix}-date-picker`}
                className="input-field flex items-center justify-between text-left"
              >
                <span className={date ? "" : "text-warmgray/50"}>
                  {date ? format(date, "EEEE, MMMM d, yyyy") : "Choose a date"}
                </span>
                <CalendarIcon className="h-5 w-5 text-brand" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => {
                  setDate(d);
                  setCalendarOpen(false);
                }}
                disabled={{ before: new Date() }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <span className="input-label">Preferred time *</span>
          <div className="flex flex-wrap gap-2">
            {TIME_SLOTS.map((s) => (
              <button
                key={s.id}
                type="button"
                data-testid={`${idPrefix}-time-${s.id}`}
                onClick={() => setSlot(s.id)}
                aria-pressed={slot === s.id}
                className={`rounded-full border-2 px-4 py-2.5 text-base font-semibold transition-all duration-200 ${
                  slot === s.id
                    ? "border-brand bg-brand text-white shadow-md shadow-brand/25"
                    : "border-espresso/15 bg-white text-espresso hover:border-brand hover:text-brand"
                }`}
              >
                {s.label} <span className="font-normal opacity-70">{s.hint}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <span className="input-label">Where does your loved one live?</span>
        <div className="flex flex-wrap gap-2">
          {SETTINGS.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`${idPrefix}-setting-${s.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setSetting(s)}
              aria-pressed={setting === s}
              className={`rounded-full border-2 px-4 py-2.5 text-base font-semibold transition-all duration-200 ${
                setting === s
                  ? "border-sage bg-sage text-white shadow-md shadow-sage/25"
                  : "border-espresso/15 bg-white text-espresso hover:border-sage hover:text-sage"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label htmlFor={`${idPrefix}-notes`} className="input-label">
          Tell us about your loved one (interests, routine, anything helpful)
        </label>
        <textarea
          id={`${idPrefix}-notes`}
          data-testid={`${idPrefix}-notes-input`}
          rows={4}
          className="input-field resize-none"
          placeholder="e.g. Mom loves gardening, crossword puzzles and stories about the old neighbourhood…"
          value={form.notes}
          onChange={set("notes")}
        />
      </div>

      <button
        type="submit"
        data-testid={`${idPrefix}-submit-button`}
        disabled={submitting}
        className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" /> Sending…
          </>
        ) : (
          "Request My Free Consultation"
        )}
      </button>
      <p className="text-center text-sm text-warmgray">
        No obligation, no pressure — just a friendly conversation about how we can help.
      </p>
    </form>
  );
};
